import { AutoComplete, Button, Empty, Form, Input, Pagination, Spin } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as CrossIcon } from 'svg/icon/close-black.svg';
import { ReactComponent as SearchIcon } from 'svg/search.svg';
import { ReactComponent as PaginationLeftIcon } from 'svg/pagination-left.svg';
import { ReactComponent as PaginationLeft2Icon } from 'svg/pagination-left-2.svg';
import { ReactComponent as PaginationRightIcon } from 'svg/pagination-right.svg';
import { ReactComponent as PaginationRight2Icon } from 'svg/pagination-right-2.svg';
import { ProductDataList } from '../shared/ProductDataList/ProductDataList';
import { getProductFieldDisplayName, isAutoField } from './services/ProductHelperService';

import styles from './GraphicProductData.module.css';


const GraphicProductData = ({
  product,
  productFields,
  feedPage,
  feedPageIndex,
  productIndex,
  reloadingProducts,
  searchProductById,
  resetProductSearch,
  toPrevProduct,
  toNextProduct,
  toSelectProduct,
  toFeedPageSelect,
  onClose,
  onDraggingElementFinish,
  inGraphicEditor = false,
  style,
}) => {
  const productsTotalCount = feedPage?.total_products ?? 0;
  const rawPageSize = feedPage?.page_size;
  const pageSize = rawPageSize > 0 ? rawPageSize : 1;
  const percent = productsTotalCount > 0 ? ((productIndex + 1) / productsTotalCount) * 100 : 0;

  const indexRef = useRef(null);
  const [productData, setProductData] = useState([]);
  const [paginationIndex, setPaginationIndex] = useState(1);
  const [form] = Form.useForm();

  const fieldSearchOptions = useMemo(() => {
    const fields = productFields ?? {};
    const keys = Object.keys(fields);
    const hasId = keys.includes('_id');
    return keys
      .filter((key) => key !== '__id' && !(key === 'id' && hasId))
      .map((key) => ({ value: key, label: key }));
  }, [productFields]);

  const defaultSearchField = useMemo(() => {
    const keys = Object.keys(productFields ?? {});
    const hasId = keys.includes('_id');
    if (hasId) return '_id';
    if (keys.includes('id')) return 'id';
    return keys[0] ?? '_id';
  }, [productFields]);

  useEffect(() => {
    const fields = productFields ?? {};
    if (!product) {
      setProductData([]);
      return;
    }
    const rows = Object.keys(product)
      .filter((key) => !key.includes('_changed') && key !== 'fresh')
      .map((key) => ({
        name: getProductFieldDisplayName(key),
        value: product[key],
        isAutoField: isAutoField(key),
        id: key,
        type: fields[key] ?? '',
      }));
    setProductData(rows);
  }, [product, productFields]);

  useEffect(() => {
    setPaginationIndex(productIndex + 1);
  }, [productIndex]);

  const setPageNumber = (index) => {
    const page = Math.floor((index - 1) / pageSize);
    if (page !== feedPageIndex) toFeedPageSelect(page);
  };

  const onPaginationPageChange = (page) => {
    setPageNumber(page);
    setPaginationIndex(page);
    toSelectProduct(page - 1);
  };

  const clickOnIndex = (e) => {
    if (reloadingProducts) return;
    if (!e || e.clientX <= 0) return;
    setProductData([]);
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const nextIndex = parseInt((x / indexRef.current.clientWidth) * productsTotalCount) + 1;
    setPageNumber(nextIndex);
    onPaginationPageChange(nextIndex);
  };

  const sortedProductData = useMemo(
    () => [...productData].sort((a, b) => (a.id > b.id ? -1 : a.id < b.id ? 1 : 0)),
    [productData],
  );

  const handleClearSearchField = () => {
    form.setFieldsValue({ searchField: defaultSearchField, searchValue: '' });
    resetProductSearch();
  };

  const handleClearSearchValue = () => {
    form.setFieldsValue({ searchValue: '' });
    resetProductSearch();
  };

  const onSearchSubmit = async ({ searchField, searchValue }) => {
    const trimmedValue = typeof searchValue === 'string' ? searchValue.trim() : searchValue;
    if (!trimmedValue) return;
    const field =
      typeof searchField === 'string' && searchField.trim() !== ''
        ? searchField.trim()
        : defaultSearchField;
    await searchProductById(undefined, field, trimmedValue);
  };

  const paginationItemRender = (current, type) => {
    switch (type) {
      case 'prev':
        return <Button className={styles.paginationNavButton}><PaginationLeftIcon /></Button>;
      case 'next':
        return <Button className={styles.paginationNavButton}><PaginationRightIcon /></Button>;
      case 'page':
        return (
          <Button
            className={`${styles.paginationNumButton} ${paginationIndex === current ? styles.paginationNumButtonActive : ''}`}
          >
            {current}
          </Button>
        );
      default:
        return '...';
    }
  };

  const showEmpty = !productsTotalCount;

  return (
    <div className={`${styles.main} ${inGraphicEditor ? styles.mainRight : ''}`} style={style}>
      <div>
        <div className={styles.header}>
          <div className={`${styles.headerText} ${inGraphicEditor ? styles.headerTextRight : ''}`}>
            Product data
          </div>
          <Button
            className={styles.headerCloseButton}
            onClick={onClose}
            icon={<CrossIcon />}
            type="text"
            shape="circle"
          />
        </div>
        <Form
          form={form}
          onFinish={onSearchSubmit}
          className={styles.search}
          initialValues={{ searchField: defaultSearchField }}
        >
          <div className={styles.searchFieldRow}>
            <Form.Item name="searchField" className={styles.searchFieldSelect}>
              <AutoComplete
                className={styles.searchAutocomplete}
                options={fieldSearchOptions}
                allowClear
                placeholder="Field"
                onClear={handleClearSearchField}
                filterOption={(inputValue, option) => {
                  const label = String(option?.label ?? '').toLowerCase();
                  const value = String(option?.value ?? '').toLowerCase();
                  const q = String(inputValue ?? '').toLowerCase();
                  return label.includes(q) || value.includes(q);
                }}
              />
            </Form.Item>
          </div>
          <div className={styles.searchValueRow}>
            <Form.Item name="searchValue" className={styles.searchItem}>
              <Input
                className={styles.searchInput}
                placeholder="Search..."
                allowClear
                onClear={handleClearSearchValue}
              />
            </Form.Item>
            <Form.Item className={styles.searchSubmitItem}>
              <Button
                className={styles.searchButton}
                icon={<SearchIcon />}
                htmlType="submit"
                shape="circle"
                type="text"
              />
            </Form.Item>
          </div>
        </Form>
      </div>

      {reloadingProducts ? (
        <div className="loading-container">
          <Spin />
        </div>
      ) : showEmpty ? (
        <div className={styles.searchEmptyWrap}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="(no data)" />
        </div>
      ) : (
        <ProductDataList
          productData={sortedProductData}
          isDraggable={inGraphicEditor}
          onDraggingElementFinish={onDraggingElementFinish}
        />
      )}

      {!showEmpty && (
        <div className={styles.footer}>
          <div
            ref={indexRef}
            onClick={clickOnIndex}
            className={styles.scrollPagination}
          >
            <div className={styles.scrollPaginationInner} style={{ width: `${percent}%` }} />
          </div>
          <div className={styles.paginationWrapper}>
            <Button
              onClick={() => toPrevProduct(10)}
              className={styles.paginationNavButton}
            >
              <PaginationLeft2Icon />
            </Button>
            <Pagination
              className={styles.pagination}
              total={productsTotalCount}
              current={paginationIndex}
              onChange={onPaginationPageChange}
              pageSize={1}
              showSizeChanger={false}
              showLessItems
              itemRender={paginationItemRender}
              size="small"
            />
            <Button
              onClick={() => toNextProduct(10)}
              className={styles.paginationNavButton}
            >
              <PaginationRight2Icon />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphicProductData;
